import { DAY_LABELS, type DaysMask, type DeviceType } from '@irrigazione/shared';

export function formatPct(value: number | null | undefined): string {
  return value == null ? '—' : `${Math.round(value)} %`;
}

export function formatNumber(value: number | null | undefined, unit = '', decimals = 1): string {
  if (value == null) return '—';
  return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
}

/** Distanza relativa in italiano, per "visto 3 min fa". */
export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return 'mai visto';
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return `${seconds} s fa`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h fa`;
  return `${Math.round(hours / 24)} g fa`;
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const time = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `oggi ${time}`;
  return `${date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} ${time}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = seconds / 60;
  return Number.isInteger(minutes) ? `${minutes} min` : `${minutes.toFixed(1)} min`;
}

export function formatDays(mask: DaysMask): string {
  if (mask === 0b1111111) return 'Tutti i giorni';
  if (mask === 0b0011111) return 'Lun-Ven';
  if (mask === 0b1100000) return 'Weekend';
  const days = DAY_LABELS.filter((_, index) => (mask & (1 << index)) !== 0);
  return days.length ? days.join(', ') : 'Mai';
}

export function formatTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const OUTCOME_LABELS: Record<string, string> = {
  running: 'In corso',
  completed: 'Completata',
  aborted_moisture: 'Saltata: terreno umido',
  aborted_water_level: 'Saltata: serbatoio scarico',
  aborted_command: 'Interrotta',
  failed: 'Fallita',
};

export function formatOutcome(outcome: string): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

const TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manuale',
  interval: 'Intervallo',
  schedule: 'Programmata',
};

/**
 * Su un irrigatore standalone l'unico avvio manuale possibile e' il pulsante
 * fisico sul controller, quindi conviene chiamarlo con il suo nome invece che
 * con il generico "Manuale".
 */
export function formatTrigger(trigger: string, deviceType?: DeviceType): string {
  if (trigger === 'manual' && deviceType === 'standalone') return 'Pulsante';
  return TRIGGER_LABELS[trigger] ?? trigger;
}

const COMMAND_STATE_LABELS: Record<string, string> = {
  pending: 'In attesa',
  sent: 'Inviato al gateway',
  acked: 'Eseguito',
  // Il gateway non e' riuscito a consegnarlo al nodo, o il nodo ha riportato un
  // guasto: in entrambi i casi l'irrigazione non e' avvenuta, ed e' quello che
  // deve risultare leggendo la tabella.
  expired: 'Scaduto, mai consegnato',
  failed: 'Non consegnato',
};

export function formatCommandState(state: string): string {
  return COMMAND_STATE_LABELS[state] ?? state;
}

/**
 * Tempo di consegna di un comando via radio: dal primo tentativo del gateway
 * alla conferma del nodo. Sotto il secondo si scrive in millisecondi, perche'
 * la differenza fra 150 e 900 ms e' quella fra "al primo colpo" e "dopo un
 * ciclo di ascolto" -- in secondi arrotondati si perderebbe.
 */
export function formatDeliveryMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}
